'use client'

import { Error } from '@mui/icons-material'
import { Alert, Autocomplete, Checkbox, FormControlLabel, Link, MenuItem, TextField, Typography } from '@mui/material'
import { useDebounce } from '@uidotdev/usehooks'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { useAtom } from 'jotai'
import { useLocale, useTranslations } from 'next-intl'
import React, { useState } from 'react'

import { IS_DEVELOPMENT } from '@/config'
import { EXTERNAL_LINKS } from '@/config/external-links'
import { useAnalytics } from '@/modules/analytics/hooks/use-analytics'
import { HAMMERTIME_API_ERRORS } from '@/modules/api/hammer-time/lib/errors'
import { trpc } from '@/modules/api/trpc/lib/browser'
import { type AutocompleteResponse } from '@/modules/api/trpc/lib/schemas'
import { BirthdayPicker } from '@/modules/auth/components/create-account-form/birthday-picker'
import { Button } from '@/modules/ui/components/button'

import classes from './create-account-form.module.scss'
import { facebookErrorsAtom } from '../../atoms'
import { useFacebookLoginMutation } from '../../hooks/use-facebook-login-mutation'
import { useSignUpMutation } from '../../hooks/use-sign-up-mutation'
import { GENDER_OPTIONS, type Gender } from '../../lib/const'
import { Recaptcha } from '../recaptcha'

const SEARCH_QUERY_MIN_LENGTH = 3
const DEBOUNCE_DELAY = 300

type AutocompleteOption = AutocompleteResponse[number]

export function CreateAccountForm() {
  const { logEvent } = useAnalytics()
  const locale = useLocale()
  const t = useTranslations('auth.createAccount.form')
  const [facebookErrors, setFacebookErrors] = useAtom(facebookErrorsAtom)
  const [email, setEmail] = useState<Nullable<string>>(null)
  const [birthdayDate, setBirthdayDate] = React.useState<string>('')
  const [gender, setGender] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [location, setLocation] = useState<Nullable<AutocompleteOption>>(null)
  const [hasAgreed, setHasAgreed] = useState(false)
  const [recaptchaToken, setRecaptchaToken] = useState<Nullable<string>>(null)
  const [isDayOpen, setDayOpen] = useState(false)
  const [isMonthOpen, setMonthOpen] = useState(false)
  const [isYearOpen, setYearOpen] = useState(false)
  const [ageError, setAgeError] = useState(false)

  const debouncedSearchQuery = useDebounce(searchQuery, DEBOUNCE_DELAY)
  const autocompleteQuery = trpc.autocomplete.useQuery(
    {
      searchQuery: debouncedSearchQuery,
      language: locale,
    },
    {
      enabled: debouncedSearchQuery.length >= SEARCH_QUERY_MIN_LENGTH,
    }
  )
  const signUpMutation = useSignUpMutation()
  const facebookMutation = useFacebookLoginMutation({
    onSuccess: () => {
      setFacebookErrors(null)
    },
  })

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!hasAgreed) {
      return
    }

    logEvent('sign_up_location_submit')

    const input = {
      birthday: birthdayDate === '' ? undefined : birthdayDate,
      gender: (gender as Maybe<Gender>) || undefined,
      mapbox: location?.mapbox ?? undefined,
      agreedWithTOSAndPP: hasAgreed,
    }

    const [yearStr, monthStr, dayStr] = birthdayDate.split('-')

    const day = Number(dayStr)
    const month = Number(monthStr)
    const year = Number(yearStr)

    if (day === 0) {
      setDayOpen(true)

      return
    }

    if (month === 0) {
      setMonthOpen(true)

      return
    }

    if (year === 0) {
      setYearOpen(true)

      return
    }

    const isUnderage = dayjs().diff(birthdayDate, 'year') < 18

    if (isUnderage) {
      setAgeError(true)

      return
    }
    setAgeError(false)

    if (facebookErrors) {
      facebookMutation.mutate({ ...input, email: email || undefined })
    } else {
      signUpMutation.mutate(input)
    }
  }

  const isSubmitDisabled = signUpMutation.isPending || !hasAgreed || (IS_DEVELOPMENT ? false : !recaptchaToken)
  const submitError = signUpMutation.error ?? facebookMutation.error

  return (
    <form className={classes.form} onSubmit={handleSubmit}>
      {submitError && (
        <Alert severity="error" icon={<Error />}>
          {submitError.message}
        </Alert>
      )}
      {ageError && (
        <Alert severity="error" icon={<Error />}>
          {t('ageRestrictionError')}
        </Alert>
      )}
      {facebookErrors?.includes(HAMMERTIME_API_ERRORS.FACEBOOK.NO_EMAIL) && (
        <TextField
          required
          name="email"
          type="email"
          label={t('email')}
          size="small"
          placeholder={t('emailPlaceholder')}
          slotProps={{ input: { readOnly: signUpMutation.isPending } }}
          onChange={(event) => setEmail(event.target.value)}
        />
      )}
      <BirthdayPicker
        birthdayDate={birthdayDate}
        setBirthdayDate={setBirthdayDate}
        facebookErrors={facebookErrors}
        signUpMutation={signUpMutation}
        isDayOpen={isDayOpen}
        setDayOpen={setDayOpen}
        isMonthOpen={isMonthOpen}
        setMonthOpen={setMonthOpen}
        isYearOpen={isYearOpen}
        setYearOpen={setYearOpen}
      />
      {(!facebookErrors || facebookErrors.includes(HAMMERTIME_API_ERRORS.FACEBOOK.NO_GENDER)) && (
        <TextField
          required
          select
          name="gender"
          className={classes.select}
          label={t('gender')}
          value={gender}
          size="small"
          slotProps={{ input: { readOnly: signUpMutation.isPending } }}
          onChange={(event) => setGender(event.target.value)}
        >
          {GENDER_OPTIONS.map((genderOption) => (
            <MenuItem key={genderOption} value={genderOption}>
              {t(`genderOptions.${genderOption}`)}
            </MenuItem>
          ))}
        </TextField>
      )}
      {(!facebookErrors || facebookErrors.includes(HAMMERTIME_API_ERRORS.FACEBOOK.NO_LOCATION)) && (
        <Autocomplete
          noOptionsText={searchQuery.length < 3 ? t('type_min_chars') : t('no_options')}
          filterOptions={(option) => option}
          value={location}
          inputValue={searchQuery}
          loading={autocompleteQuery.isLoading}
          options={autocompleteQuery.data ?? []}
          readOnly={signUpMutation.isPending}
          size="small"
          onChange={(_event, value) => setLocation(value ?? null)}
          onInputChange={(_event, value) => setSearchQuery(value)}
          getOptionLabel={(option) => `${option.name}`}
          renderInput={(params) => (
            <TextField {...params} name="city" required label={t('city')} placeholder={t('cityPlaceholder')} />
          )}
        />
      )}

      <div>
        <Typography component="p" variant="body2">
          {t.rich('agreementDetails', {
            terms: (chunks) => (
              <Link underline="hover" href={EXTERNAL_LINKS.terms} target="_blank" rel="noopener noreferrer">
                {chunks}
              </Link>
            ),
            privacy: (chunks) => (
              <Link underline="hover" href={EXTERNAL_LINKS.privacy} target="_blank" rel="noopener noreferrer">
                {chunks}
              </Link>
            ),
          })}
        </Typography>

        <div className={classes.checkboxWrapper}>
          <FormControlLabel
            name="agree"
            label={t('agree')}
            control={
              <Checkbox
                value={hasAgreed}
                checked={hasAgreed}
                readOnly={signUpMutation.isPending}
                onChange={(event) => setHasAgreed(event.target.checked)}
              />
            }
          />
        </div>
        <Recaptcha
          analyticsPrefix="sign_up_recaptcha"
          onChange={setRecaptchaToken}
          className={clsx(classes.recaptcha, hasAgreed && classes.visible)}
        />

        <Button
          analytics={{
            eventName: 'create_account',
          }}
          type="submit"
          variant="contained"
          color="secondary"
          size="large"
          disabled={isSubmitDisabled}
        >
          {signUpMutation.isPending ? t('submitting') : t('submit')}
        </Button>
      </div>
    </form>
  )
}
